import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRow({ value, onChange, size = 18, interactive = false }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange?.(n)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          data-testid={interactive ? `rating-star-${n}` : undefined}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star
            style={{ width: size, height: size }}
            className={n <= value ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#CAD2C5]'}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ProductReviews({ productId }) {
  const [data, setData] = useState({ count: 0, average: 0, reviews: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ author_name: '', rating: 5, text: '', location: '' });

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/products/${productId}/reviews`);
      setData(r.data);
    } catch (err) {
      logger.error('reviews fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.text.trim()) {
      toast.error('Please fill in your name and review');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${productId}/reviews`, form);
      toast.success('Thanks for sharing your review!');
      setShowForm(false);
      setForm({ author_name: '', rating: 5, text: '', location: '' });
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-[#CAD2C5]/50 mt-16 pt-12" data-testid="product-reviews">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-caption text-[#84A98C] mb-2">What customers say</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#354F52]">
            Reviews {data.count > 0 && <span className="text-[#84A98C] text-base">({data.count})</span>}
          </h2>
          {data.count > 0 && (
            <div className="flex items-center gap-2 mt-2" data-testid="reviews-summary">
              <StarRow value={Math.round(data.average)} />
              <span className="text-sm text-[#52796F]">{data.average.toFixed(1)} out of 5</span>
            </div>
          )}
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="btn-secondary"
          data-testid="write-review-btn"
        >
          {showForm ? 'Cancel' : 'Write a review'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#F5F5F0] p-6 rounded-sm mb-10 space-y-4" data-testid="review-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="r-name">Your name *</Label>
              <Input
                id="r-name"
                value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                placeholder="Lindy L."
                data-testid="review-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-loc">Location (optional)</Label>
              <Input
                id="r-loc"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Paphos, Cyprus"
                data-testid="review-location-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Your rating *</Label>
            <StarRow
              value={form.rating}
              onChange={(n) => setForm({ ...form, rating: n })}
              interactive
              size={24}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-text">Your review *</Label>
            <Textarea
              id="r-text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={4}
              placeholder="Share how you wear it, who you gifted it to, or how it made you feel..."
              data-testid="review-text-input"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-review-btn">
              {submitting ? 'Posting...' : 'Post review'}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-[#84A98C] text-sm">Loading reviews...</p>
      ) : data.reviews.length === 0 ? (
        <div className="text-center py-10 bg-[#F5F5F0] rounded-sm">
          <p className="text-[#52796F] mb-1">No reviews yet — be the first to share your story.</p>
          <p className="text-xs text-[#84A98C]">Every review helps another bracelet find its home.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.reviews.map((r) => (
            <div
              key={r.id}
              className="border-b border-[#CAD2C5]/40 pb-6 last:border-0"
              data-testid={`review-${r.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-3">
                  <p className="font-serif text-[#354F52]">{r.author_name}</p>
                  <StarRow value={r.rating} size={14} />
                </div>
                <p className="text-xs text-[#84A98C]">
                  {r.location ? `${r.location} · ` : ''}{formatDate(r.created_at)}
                </p>
              </div>
              <p className="text-[#52796F] leading-relaxed whitespace-pre-line">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
